/**
 * Converts Markdown text to safe HTML for display.
 * Follows basic syntax from https://www.markdownguide.org/basic-syntax/
 *
 * Supports:
 * - Headings: # through ###### (with space after #)
 * - Bold: **text** or __text__
 * - Italic: *text* or _text_
 * - Bold and italic: ***text*** or ___text___
 * - Blockquotes: > line
 * - Unordered lists: - * +
 * - Ordered lists: 1. 2. 3.
 * - Horizontal rules: --- *** ___
 * - Inline code: `code`
 * - Links: [text](url) and [text](url "title")
 * - Line breaks: two trailing spaces → <br>, blank line → new paragraph
 * - Escaping: \ for * _ # etc.
 * - Images: ![alt](url) (rendered as img with class blog-markdown-img)
 */

const HTML_ESCAPE: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => HTML_ESCAPE[ch] ?? ch);
}

/** Process inline markdown: bold, italic, code, links. Applied to already HTML-escaped text. */
function processInlineMarkdown(s: string): string {
  // Process escaped characters first: \* \_ \# etc. → remove backslash, keep char
  let out = s.replace(/\\([\\`*_{}\[\]()#.+>-])/g, "$1");

  // Inline code: `code` - use non-greedy match so we don't cross backticks
  out = out.replace(/`([^`]+)`/g, '<code class="blog-markdown-code">$1</code>');

  // Images: ![alt](url) - before links so we don't match the same brackets
  out = out.replace(/!\[([^\]]*)\]\(\s*([^\s)]+)\s*\)/g, (_, alt, url) => {
    const safeUrl = escapeHtml(url.trim());
    const safeAlt = escapeHtml(alt);
    return `<img class="blog-markdown-img" src="${safeUrl}" alt="${safeAlt}" loading="lazy" />`;
  });

  // Links: [text](url) or [text](url "title") - title optional
  out = out.replace(
    /\[([^\]]*)\]\(\s*([^\s)]+)(?:\s+"([^"]*)")?\s*\)/g,
    (_, text, url, title) => {
      const safeUrl = escapeHtml(url);
      const safeTitle = title ? ` title="${escapeHtml(title)}"` : "";
      return `<a class="blog-markdown-link" href="${safeUrl}"${safeTitle} target="_blank">${text}</a>`;
    },
  );

  // Bold and italic: ***text*** or ___text___ (three chars)
  out = out.replace(/\*\*\*([^*]+)\*\*\*/g, "<strong><em>$1</em></strong>");
  out = out.replace(/___([^_]+)___/g, "<strong><em>$1</em></strong>");

  // Bold: **text** or __text__ (must not be part of *** or ___)
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/__([^_]+)__/g, "<strong>$1</strong>");

  // Italic: *text* or _text_ (single; word boundaries per guide)
  out = out.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  out = out.replace(/_([^_]+)_/g, "<em>$1</em>");

  return out;
}

/** Optional: wrap content in a classed container for consistent styling (e.g. blog-content). */
const BLOCK_CLASS = "blog-markdown";

function isHorizontalRule(trimmed: string): boolean {
  return (
    /^---+$/.test(trimmed) ||
    /^\*\*\*+$/.test(trimmed) ||
    /^___+$/.test(trimmed)
  );
}

function getHeadingLevel(trimmed: string): number {
  const m = trimmed.match(/^(#{1,6})\s+/);
  return m ? m[1].length : 0;
}

function isBlockquote(trimmed: string): boolean {
  return trimmed.startsWith("> ");
}

function getBlockquoteContent(trimmed: string): string {
  return trimmed.slice(2).trim();
}

function isUnorderedList(trimmed: string): boolean {
  return /^[-*+]\s+/.test(trimmed);
}

function getUnorderedListContent(trimmed: string): string {
  return trimmed.replace(/^[-*+]\s+/, "");
}

function isOrderedList(trimmed: string): boolean {
  return /^\d+\.\s+/.test(trimmed);
}

function getOrderedListContent(trimmed: string): string {
  return trimmed.replace(/^\d+\.\s+/, "");
}

/**
 * Convert Markdown to safe HTML. Use for title, headline/subtitle, summary, content, quote.
 */
export function formatDescriptionMarkdown(text: string): string {
  if (!text || typeof text !== "string") return "";

  const lines = text.split(/\r?\n/);
  const out: string[] = [];
  let inBlockquote = false;
  let inList: "ul" | "ol" | false = false;
  let inParagraph = false;

  const closeParagraph = () => {
    if (inParagraph) {
      out.push("</p>");
      inParagraph = false;
    }
  };

  const closeList = () => {
    if (inList) {
      out.push(inList === "ul" ? "</ul>" : "</ol>");
      inList = false;
    }
  };

  const closeBlockquote = () => {
    if (inBlockquote) {
      out.push("</blockquote>");
      inBlockquote = false;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const trimmed = rawLine.trim();
    const lineEndSpaces =
      rawLine !== rawLine.trimEnd() && / \s+$/.test(rawLine);

    if (trimmed === "") {
      closeParagraph();
      closeList();
      closeBlockquote();
      out.push("<br/>");
      continue;
    }

    // Horizontal rule: --- *** ___
    if (isHorizontalRule(trimmed)) {
      closeParagraph();
      closeList();
      closeBlockquote();
      out.push(`<hr class="${BLOCK_CLASS}-hr" />`);
      continue;
    }

    // Blockquote: > line
    if (isBlockquote(trimmed)) {
      closeParagraph();
      closeList();
      const content = escapeHtml(getBlockquoteContent(trimmed));
      if (!inBlockquote) {
        closeBlockquote();
        out.push(`<blockquote class="${BLOCK_CLASS}-blockquote">`);
        inBlockquote = true;
      } else {
        out.push("<br/>");
      }
      out.push(processInlineMarkdown(content));
      continue;
    }

    if (inBlockquote) {
      closeBlockquote();
    }

    // Headings: # through ###### (space required after #)
    const headingLevel = getHeadingLevel(trimmed);
    if (headingLevel >= 1 && headingLevel <= 6) {
      closeParagraph();
      closeList();
      const rest = trimmed.slice(headingLevel).trim();
      const escaped = escapeHtml(rest);
      const plainForCompare = rest
        .replace(/[*_`#\[\]()]/g, "")
        .trim()
        .toLowerCase();
      const isPanaceaTakeaway = plainForCompare === "panacea takeaway";
      const extraClass = isPanaceaTakeaway ? " panacea-takeaway-header" : "";
      out.push(
        `<h${headingLevel} class="${BLOCK_CLASS}-h${headingLevel}${extraClass}">${processInlineMarkdown(escaped)}</h${headingLevel}>`,
      );
      continue;
    }

    // Unordered list: - * +
    if (isUnorderedList(trimmed)) {
      closeParagraph();
      const content = escapeHtml(getUnorderedListContent(trimmed));
      if (inList !== "ul") {
        closeList();
        out.push(`<ul class="${BLOCK_CLASS}-ul">`);
        inList = "ul";
      }
      out.push("<li>" + processInlineMarkdown(content) + "</li>");
      continue;
    }

    // Ordered list: 1. 2.
    if (isOrderedList(trimmed)) {
      closeParagraph();
      const content = escapeHtml(getOrderedListContent(trimmed));
      if (inList !== "ol") {
        closeList();
        out.push(`<ol class="${BLOCK_CLASS}-ol">`);
        inList = "ol";
      }
      out.push("<li>" + processInlineMarkdown(content) + "</li>");
      continue;
    }

    closeList();

    // Paragraph (also catches "Panacea Takeaway" when it appears as first line without #)
    const escaped = escapeHtml(trimmed);
    const plainForCompareP = trimmed
      .replace(/[*_`#\[\]()]/g, "")
      .trim()
      .toLowerCase();
    const isPanaceaTakeawayBlock = plainForCompareP === "panacea takeaway";
    const extraClassP = isPanaceaTakeawayBlock
      ? " panacea-takeaway-header"
      : "";
    if (!inParagraph) {
      out.push(`<p class="${BLOCK_CLASS}-p${extraClassP}">`);
      inParagraph = true;
    } else {
      out.push(" ");
    }
    out.push(processInlineMarkdown(escaped));
    if (lineEndSpaces) {
      out.push("<br/>");
    }
  }

  closeParagraph();
  closeList();
  closeBlockquote();
  return out.join("").replace(/(<br\/>)+$/, "");
}
